/* eslint-disable jsx-a11y/no-static-element-interactions, react/no-array-index-key */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropdown from 'components/Dropdown';
import { Link } from 'react-router';
import Loader from 'components/Loader';
import _ from 'lodash';
import classNames from 'classnames';
import { SITE_PLATFORM_WEB, SITE_PLATFORM_WEB_LARGE } from 'helpers/BreakpointHelper';
import { sportsFeatures, isSportsContent } from 'helpers/ContentHelper';
import { scroller, Element } from 'react-scroll';
import {
  OVERLAY_LIVE_DATES,
  OVERLAY_LIVE_NETWORKS,
  OVERLAY_LIVE_CTA_OVERFLOW,
} from 'helpers/FilterHelper';
import ScheduleItemComponent from 'screens/Live/components/ScheduleItem';
import {
  MINUTES_INTERVAL,
  PANEL_TYPE_LISTING,
  getNetworkLogo,
  getHourInterval,
  getStartingTs,
  displayPrimeTimeMessage,
  getPrimeTimeMessage,
  EPG_TOTAL_DAYS,
  checkForOverlap,
  getDateDropdownFormat,
  getDateDropdownIcon,
  isLastPanel,
  isReturningVistor,
} from 'screens/Live/LiveHelpers';
import {
  FORMAT_WEEKDAY_MONTH_DAY,
  format,
  now,
  stringToDate,
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  getHours,
  getMinutes,
  isSameDay,
  isAfter,
  subHours,
  setHours,
} from 'helpers/DateTimeHelper';
import CurrentTimerComponent from 'screens/Live/components/CurrentTimer';
import dropdownTheme from 'screens/Live/styles/Dropdown.css';
import Style from '../../Live.module.scss';

const EPG_SCROLL_HOUR_JUMP = 2; // jump two hours.
const EPG_JUMPS_PER_DAY = 24 / EPG_SCROLL_HOUR_JUMP;

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    intervalCount: 6,
  },
  [SITE_PLATFORM_WEB]: {
    intervalCount: 4,
  },
};

class ScheduleGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startingTs: getStartingTs(),
      openRow: null,
      dropdownDateClosed: null,
      dropdownNetworkOpen: false,
      epgGridPage: 1,
      startingDate: now(),
      endingDate: addDays(now(), EPG_TOTAL_DAYS - 1),
    };

    this.toggleNetworkDropdown = this.toggleNetworkDropdown.bind(this);
    this.toggleDateDropdown = this.toggleDateDropdown.bind(this);
    this.currentTimerEnded = this.currentTimerEnded.bind(this);
    this.getSelectedDate = this.getSelectedDate.bind(this);
    this.changeDate = this.changeDate.bind(this);
  }

  componentDidMount() {
    const selectedDate = this.getSelectedDate() || now();
    this.changeDate({ date: selectedDate });
    this.scrollToPreviousGridElement();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const customizer = (objValue, othValue) => {
      if (_.isFunction(objValue) && _.isFunction(othValue)) {
        return true;
      }
      return false;
    };
    if (!_.isEqualWith(this.props, nextProps, customizer)) return true;
    if (!_.isEqual(this.state, nextState, customizer)) return true;
    return false;
  }

  getDropdownItems() {
    const items = [];
    const selectedDate = this.getSelectedDate();
    const hour = getHours(selectedDate);

    // create the 7 day options.
    for (let i = 0; i < EPG_TOTAL_DAYS; i += 1) {
      const date = setHours(addDays(now(), i), hour);
      const isSelected = isSameDay(selectedDate);
      items.push({
        name: format(date, FORMAT_WEEKDAY_MONTH_DAY),
        onClickParams: date,
        onClick: date => {
          this.changeDate({ date });
          this.toggleDateDropdown();
        },
        isSelected,
      });
    }
    return items;
  }

  getSelectedDate() {
    const { selectedDate } = this.props;
    return selectedDate === null ? now() : stringToDate(selectedDate);
  }

  changeDate({ date, resetTimer = true }) {
    const { setSelectedDate } = this.props;
    setSelectedDate(stringToDate(date), resetTimer);
    this.setState({ openRow: null, dropdownDateClosed: null, epgGridPage: 1 });
  }

  checkAtStarting(date) {
    const { startingDate } = this.state;

    const startingHours = getHours(startingDate);
    const startingMinutes = getMinutes(startingDate);
    const startingTime = startingHours * 60 + Math.floor(startingMinutes / MINUTES_INTERVAL);

    const dateHours = getHours(date);
    const dateMinutes = getMinutes(startingDate);
    const time = dateHours * 60 + Math.floor(dateMinutes / MINUTES_INTERVAL);

    return isSameDay(startingDate, date) && startingTime >= time;
  }

  checkSameDay(date) {
    const selectedDate = this.getSelectedDate();
    const result = isSameDay(selectedDate, date);
    return result;
  }

  closeDateDropdown() {
    const { overlay } = this.props;
    if (overlay === OVERLAY_LIVE_DATES) {
      this.toggleDateDropdown();
    }
  }

  currentTimerEnded() {
    this.changeDate({ resetTimer: false });
    this.setState({
      startingTs: getStartingTs(),
      startingDate: now(),
      epgGridPage: 1,
      openRow: null,
      dropdownDateClosed: null,
    });
  }

  gridNext() {
    const { epgGridPage } = this.state;
    const { resetStillWatchingTimeout } = this.props;
    const date = this.getSelectedDate();
    const newDate = addHours(date, EPG_SCROLL_HOUR_JUMP);
    let newEpgGridPage = epgGridPage + 1;
    if (!this.checkSameDay(newDate)) newEpgGridPage = 1;

    this.closeDateDropdown();
    this.changeDate({ date: newDate });
    this.setState({ openRow: null, dropdownDateClosed: null, epgGridPage: newEpgGridPage });
    resetStillWatchingTimeout();
  }

  gridPrev() {
    const { epgGridPage, startingDate } = this.state;
    const { resetStillWatchingTimeout } = this.props;
    const date = this.getSelectedDate();
    let newDate = subHours(date, EPG_SCROLL_HOUR_JUMP);
    let newEpgGridPage = epgGridPage - 1;
    if (!this.checkSameDay(newDate)) newEpgGridPage = EPG_JUMPS_PER_DAY;
    if (this.checkAtStarting(newDate)) newDate = startingDate;

    this.closeDateDropdown();
    this.changeDate({ date: newDate });
    this.setState({ openRow: null, dropdownDateClosed: null, epgGridPage: newEpgGridPage });
    resetStillWatchingTimeout();
  }

  isWeb() {
    const { breakpoint } = this.props;
    return breakpoint === SITE_PLATFORM_WEB || breakpoint === SITE_PLATFORM_WEB_LARGE;
  }

  scheduleItemDetailsCB(rowIdx, action) {
    const newOpenRow = action === 'close' ? null : rowIdx;
    this.setState({ openRow: newOpenRow, dropdownDateClosed: null });
  }

  scrollToPreviousGridElement() {
    const { prevRowIdx, location } = this.props;
    if (prevRowIdx && isReturningVistor(location)) {
      scroller.scrollTo(`${prevRowIdx}`, { duration: 300, offset: 0, smooth: true });
      this.setState({ openRow: prevRowIdx, dropdownDateClosed: null });
    }
  }

  toggleDateDropdown(close) {
    const { resetStillWatchingTimeout, setOverlay } = this.props;
    setOverlay(OVERLAY_LIVE_DATES);
    resetStillWatchingTimeout();

    if (close) {
      this.setState({ dropdownDateClosed: true });
    }
  }

  toggleNetworkDropdown() {
    const { resetStillWatchingTimeout, setOverlay } = this.props;
    setOverlay(OVERLAY_LIVE_NETWORKS);
    resetStillWatchingTimeout();
  }

  renderDateDropdown() {
    const { overlay } = this.props;
    const isOpen = overlay === OVERLAY_LIVE_DATES;
    const selectedDate = this.getSelectedDate();
    const dropDownformat = getDateDropdownFormat(isOpen && !this.isWeb());
    const items = this.getDropdownItems();
    const defaultText = selectedDate ? format(selectedDate, dropDownformat) : items[0].name;

    return (
      <Dropdown
        theme={dropdownTheme}
        items={items}
        getIcon={getDateDropdownIcon(isOpen)}
        defaultText={defaultText}
        onOpenDropdown={this.toggleDateDropdown}
        onCloseDropdown={() => this.toggleDateDropdown(true)}
        displayResults={isOpen}
        showMobileCancel={!this.isWeb()}
        expanded={isOpen}
        className={classNames(Style.dropdown, dropdownTheme.dateDropdown)}
      />
    );
  }

  renderDateLabel() {
    const { overlay } = this.props;
    const { epg_todayHeader: todayHeader, epg_tomorrowHeader: tomorrowHeader } = window.Language;
    const isOpen = overlay === OVERLAY_LIVE_DATES;
    if (isOpen) return null;

    let header = null;
    const daysToAdd = 1;
    const selectedDate = this.getSelectedDate();
    const today = now();
    const tomorrow = addDays(now(), daysToAdd);
    const isToday = isSameDay(selectedDate, today);
    const isTomorrow = isSameDay(selectedDate, tomorrow);

    if (isToday) header = todayHeader;
    if (isTomorrow) header = tomorrowHeader;
    if (!header) return null;
    return <div className={Style.tabDateLabel}>{header}</div>;
  }

  renderNetworkDropdown() {
    const { overlay, setSelectedDate } = this.props;
    const isOpen = overlay === OVERLAY_LIVE_NETWORKS;
    const { panels, isStatic, selectedChannel, onChannelSelect } = this.props;
    let defaultNetworkLogoElement = null;
    const items = panels.member.map((item, rowIdx) => {
      const { panelType } = item;
      const firstItem = _.get(item, 'items.member.0');
      const hasSports = firstItem && isSportsContent(firstItem);
      if (panelType !== PANEL_TYPE_LISTING || (!sportsFeatures() && hasSports)) return null;

      const { callSign } = item;
      const network = _.get(item, 'items.member.0.network', null);
      const networkLogo = getNetworkLogo({ callSign, network, isStatic });
      let networkLogoElement = null;
      if (networkLogo) {
        networkLogoElement = (
          <img
            className={Style.networkLogo}
            src={networkLogo}
            alt={callSign}
            aria-label={callSign}
          />
        );
      }
      const isSelected = selectedChannel === rowIdx;
      if (isSelected) defaultNetworkLogoElement = networkLogoElement;

      return {
        onClickParams: rowIdx,
        onClick: lclRowIdx => {
          setSelectedDate(now());
          onChannelSelect({ selectedChannel: lclRowIdx });
          this.toggleNetworkDropdown();
        },
        labelDetail: networkLogoElement,
        isSelected,
      };
    });
    const defaultText = defaultNetworkLogoElement;

    return (
      <Dropdown
        theme={dropdownTheme}
        items={items}
        getIcon={getDateDropdownIcon(isOpen)}
        defaultText={defaultText}
        onOpenDropdown={this.toggleNetworkDropdown}
        onCloseDropdown={this.toggleNetworkDropdown}
        displayResults={isOpen}
        showMobileCancel={!this.isWeb()}
        expanded={isOpen}
        useLabelDetail
        className={classNames(Style.dropdown, dropdownTheme.networkDropdown)}
      />
    );
  }

  renderPanels() {
    const {
      setOverlay,
      overlay,
      panels,
      onChannelSelect,
      selectedChannel,
      account,
      resetStillWatchingTimeout,
      isAuthenticated,
      preauthorizedResources,
      saveLiveRestart,
      breakpoint,
      loadingGrid,
      isStatic,
      setSelectedDate,
      selectedVideoData,
      favoritesManager,
      location,
      defaultProgram,
    } = this.props;
    const { openRow, epgGridPage, dropdownDateClosed } = this.state;
    const selectedDate = this.getSelectedDate();
    const breakpointIsMobile = !this.isWeb();
    const dropdownZIndex =
      overlay === OVERLAY_LIVE_CTA_OVERFLOW
        ? dropdownTheme.openedZIndex
        : dropdownTheme.closedZIndex;
    if (!panels || !panels.member || panels.length === 0) return null;
    return panels.member.map((item, rowIdx) => {
      const { panelType } = item;
      const firstItem = _.get(item, 'items.member.0');
      const hasSports = firstItem && isSportsContent(firstItem);
      if (breakpointIsMobile && selectedChannel !== rowIdx) return null;
      if (panelType !== PANEL_TYPE_LISTING || (!sportsFeatures() && hasSports)) return null;
      const { callSign } = item;
      const network = _.get(item, 'items.member.0.network', null);
      const networkLogo = getNetworkLogo({ callSign, network, isStatic });
      let networkLogoElement = null;
      if (networkLogo) {
        networkLogoElement = (
          <img
            className={Style.networkLogo}
            src={networkLogo}
            alt={callSign}
            aria-label={item.callSign}
          />
        );
      }

      const primeTimeMessage = getPrimeTimeMessage({
        panels,
        selectedChannel: rowIdx,
        selectedDate,
      });
      const { lateProgramIdx, overFlowProgramIdx, overFlowPrograms } = checkForOverlap(item);

      let scheduleRowClassName =
        openRow === rowIdx
          ? classNames(Style.scheduleRow, Style.scheduleRowOpen)
          : Style.scheduleRow;
      scheduleRowClassName =
        (!selectedChannel && rowIdx === 0) || rowIdx === selectedChannel
          ? classNames(scheduleRowClassName, Style.scheduleRowSelected)
          : scheduleRowClassName;

      const scheduleItemProps = {
        setOverlay,
        overlay,
        network,
        account,
        startingTs: getStartingTs(selectedDate),
        openRow,
        scheduleItemDetailsCB: (idx, action) => {
          this.scheduleItemDetailsCB(idx, action);
        },
        switchChannelCB: channel => {
          onChannelSelect({ selectedChannel: channel, clearQuery: true });
        },
        callSign,
        selectedDate,
        displayPrimeTimeMessage,
        dropdownDateClosed,
        epgGridPage,
        primeTimeMessage,
        resetStillWatchingTimeout,
        preauthorizedResources,
        isAuthenticated,
        breakpoint,
        saveLiveRestart,
        loadingGrid,
        breakpointIsMobile,
        lateProgramIdx,
        overFlowProgramIdx,
        selectedVideoData,
        favoritesManager,
        location,
      };

      const scheduleGridComponent = (
        <Element className={Style.scheduleItemWrapper} name={`${rowIdx}`}>
          <ScheduleItemComponent
            {...scheduleItemProps}
            item={item}
            rowIdx={rowIdx}
            isOverFlow={false}
          />
        </Element>
      );
      const programs = overFlowPrograms || [];
      const overflowRows = [];
      if (!breakpointIsMobile && sportsFeatures()) {
        let overFlowScheduleRowClassName = null;
        let overFlowRowIdx = 0;
        programs.forEach(overFlowProgram => {
          overFlowRowIdx += 1;
          scheduleItemProps.overFlowProgramIdx = overFlowRowIdx;
          const overFlowItem = _.cloneDeep(item);
          const endDateIsAfter = isAfter(stringToDate(overFlowProgram.endDate), selectedDate);

          if (endDateIsAfter) {
            overFlowItem.items.member = [_.cloneDeep(overFlowProgram)];

            overFlowScheduleRowClassName =
              openRow === `${rowIdx}-${overFlowRowIdx}`
                ? classNames(Style.scheduleRow, Style.scheduleRowOpen, Style.scheduleRowSelected)
                : Style.scheduleRow;
            if (
              defaultProgram === overFlowProgram.id ||
              (selectedVideoData && overFlowProgram.id === selectedVideoData.id)
            ) {
              overFlowScheduleRowClassName = classNames(
                overFlowScheduleRowClassName,
                Style.scheduleRowSelected,
              );
            }

            scheduleItemProps.switchChannelCB = idx => {
              onChannelSelect({
                selectedChannel: idx,
                id: overFlowItem.id,
                programId: overFlowProgram.id,
                overFlowRowIdx: idx,
                clearQuery: false,
              });
            };
            overflowRows.push(
              <div
                key={`schedulerow-${rowIdx}-${overFlowRowIdx}`}
                className={overFlowScheduleRowClassName}
              >
                <Link
                  className={Style.scheduleLeft}
                  onClick={() => {
                    setSelectedDate(now());
                    onChannelSelect({
                      selectedChannel: `${rowIdx}-${overFlowRowIdx}`,
                      id: overFlowItem.id,
                      programId: overFlowProgram.id,
                      overFlowRowIdx,
                      clearQuery: false,
                    });
                  }}
                >
                  {networkLogoElement}
                </Link>
                <Element className={Style.scheduleItemWrapper} name={`${rowIdx}-${overFlowRowIdx}`}>
                  <ScheduleItemComponent
                    {...scheduleItemProps}
                    item={overFlowItem}
                    rowIdx={`${rowIdx}-${overFlowRowIdx}`}
                    overFlowProgramIdx={`${rowIdx}-${overFlowRowIdx}`}
                    isOverFlow
                    name="overflow"
                  />
                </Element>
              </div>,
            );
          }
        });
      }

      return (
        <div key={`schedulerow-${rowIdx}`}>
          <div className={dropdownZIndex}>
            <div className={scheduleRowClassName}>
              <Link
                className={Style.scheduleLeft}
                onClick={() => {
                  setSelectedDate(now());
                  onChannelSelect({ selectedChannel: rowIdx, clearQuery: true });
                }}
              >
                {networkLogoElement}
              </Link>
              {scheduleGridComponent}
            </div>
          </div>
          {overflowRows}
        </div>
      );
    });
  }

  renderTimeline() {
    const { breakpoint } = this.props;
    const { intervalCount } = BREAKPOINTS[breakpoint];
    const selectedDate = this.getSelectedDate();
    const dateIsInFuture = isAfter(endOfDay(now()));
    const newDate = dateIsInFuture ? startOfDay(selectedDate) : selectedDate;
    const hourIntervals = getHourInterval(newDate, MINUTES_INTERVAL, intervalCount);
    const intervalElement = hourIntervals.map((obj, idx) => (
      <div className={Style.time} key={`timeline-${idx}`}>
        {obj.timeString}
      </div>
    ));

    return (
      <div id="timeline-main" className={Style.timelineMain}>
        {intervalElement}
      </div>
    );
  }

  render() {
    const { loadingGrid, panels, overlay } = this.props;
    const { endingDate, epgGridPage, openRow, startingTs } = this.state;
    const selectedDate = this.getSelectedDate();
    const atStartDate = this.checkAtStarting(selectedDate);
    const atEndDate = epgGridPage >= EPG_JUMPS_PER_DAY && this.checkSameDay(endingDate);
    const noScroll = overlay ? Style.noScroll : null;
    const dropdownZIndex =
      overlay === OVERLAY_LIVE_NETWORKS || overlay === OVERLAY_LIVE_DATES
        ? dropdownTheme.openedZIndex
        : dropdownTheme.closedZIndex;
    if (!panels) return null;
    if (!this.isWeb()) {
      return (
        <div className={classNames(noScroll, [Style.container, Style.scheduleGrid])}>
          <div className={classNames(Style.mobileDropdowns, dropdownZIndex)}>
            {this.renderNetworkDropdown()}
            {this.renderDateLabel()}
            {this.renderDateDropdown()}
          </div>
          {this.renderPanels()}
        </div>
      );
    }

    const gridLoadingClass = loadingGrid ? Style.gridLoading : null;
    return (
      <div className={classNames([Style.container, Style.scheduleGrid, noScroll])}>
        <div className={classNames([Style.timeline, gridLoadingClass])}>
          <div className={Style.timelineLeft}>{this.renderDateDropdown()}</div>
          {this.renderTimeline()}
          {atStartDate ? (
            <div className={Style.timelineEmptyArrow} />
          ) : (
            <Link
              aria-label="Previous"
              className={classNames(Style.timelineArrow, Style.timelineLeftArrow)}
              onClick={() => {
                this.gridPrev();
              }}
            />
          )}
          {atEndDate ? (
            <div className={Style.timelineEmptyArrow} />
          ) : (
            <Link
              aria-label="Next"
              className={classNames(Style.timelineArrow, Style.timelineRightArrow)}
              onClick={() => {
                this.gridNext();
              }}
            />
          )}
        </div>
        {loadingGrid ? <Loader className={Style.gridLoader} /> : null}
        <div className={gridLoadingClass}>{this.renderPanels()}</div>
        {atStartDate ? (
          <CurrentTimerComponent
            ended={() => this.currentTimerEnded()}
            startingTs={startingTs}
            totalMinutes={MINUTES_INTERVAL}
            isLastPanel={isLastPanel(openRow, panels)}
            openRow={openRow}
            totalRows={panels.totalItems}
          />
        ) : null}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { prevRowIdx } = state.live;
  return { prevRowIdx };
}

ScheduleGrid.defaultProps = {
  account: {},
  breakpoint: null,
  favoritesManager: null,
  isAuthenticated: null,
  isStatic: false,
  loadingGrid: false,
  location: {},
  overlay: null,
  panels: null,
  preauthorizedResources: [],
  prevRowIdx: null,
  selectedChannel: 0,
  selectedDate: null,
  selectedVideoData: null,
  defaultProgram: null,
};

ScheduleGrid.propTypes = {
  account: PropTypes.shape({}),
  panels: PropTypes.shape({
    itemsPerPage: PropTypes.number,
    member: PropTypes.arrayOf(PropTypes.shape({})),
    totalItems: PropTypes.number,
  }),
  setOverlay: PropTypes.func.isRequired,
  overlay: PropTypes.string,
  onChannelSelect: PropTypes.func.isRequired,
  selectedChannel: PropTypes.number,
  setSelectedDate: PropTypes.func.isRequired,
  saveLiveRestart: PropTypes.func.isRequired,
  selectedDate: PropTypes.shape({}),
  isStatic: PropTypes.bool,
  resetStillWatchingTimeout: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  breakpoint: PropTypes.string,
  loadingGrid: PropTypes.bool,
  selectedVideoData: PropTypes.shape({}),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    setFavorites: PropTypes.func.isRequired,
    deleteFavorites: PropTypes.func.isRequired,
  }),
  location: PropTypes.shape({}),
  prevRowIdx: PropTypes.number,
  defaultProgram: PropTypes.string,
};

export default connect(mapStateToProps)(ScheduleGrid);
